import React, { useState, useEffect, useCallback } from 'react';
import { supabaseClient } from '../../services/supabase';
import HolidayCalendar from './HolidayCalendar';

function Holidays() {
  const [selectedHolidays, setSelectedHolidays] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [selectedWeekdays, setSelectedWeekdays] = useState([]);

  const formatYM = (date) => {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
  };

  const formatYMD = (date) => {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  };

  const loadHolidays = useCallback(async () => {
    try {
      const { data, error } = await supabaseClient
        .from('holidays')
        .select('holiday_date')
        .order('holiday_date', { ascending: true });

      if (error) {
        console.error('休日データ読み込みエラー:', error);
        return;
      }

      const today = new Date();
      const targetMonth = new Date(today.getFullYear(), today.getMonth() + 2, 1);
      const targetPrefix = formatYM(targetMonth);
      const needsInit = !data || !data.some((h) => h.holiday_date.startsWith(targetPrefix));

      if (needsInit) {
        const cutoff = new Date(today.getFullYear(), today.getMonth(), 1);
        const cutoffStr = formatYMD(cutoff);

        const { error: delError } = await supabaseClient
          .from('holidays')
          .delete()
          .lt('holiday_date', cutoffStr);

        if (delError) console.error('古い休日データ削除エラー:', delError);

        const lastDay = new Date(targetMonth.getFullYear(), targetMonth.getMonth() + 1, 0).getDate();
        const mondays = [];
        for (let day = 1; day <= lastDay; day++) {
          const d = new Date(targetMonth.getFullYear(), targetMonth.getMonth(), day);
          if (d.getDay() === 1) {
            mondays.push({ holiday_date: formatYMD(d) });
          }
        }

        if (mondays.length > 0) {
          const { error: insError } = await supabaseClient
            .from('holidays')
            .insert(mondays);
          if (insError) console.error('月曜休日自動登録エラー:', insError);
        }

        const { data: refreshed, error: refError } = await supabaseClient
          .from('holidays')
          .select('holiday_date')
          .order('holiday_date', { ascending: true });

        if (refError) {
          console.error('休日データ再読み込みエラー:', refError);
          return;
        }

        const holidaysSet = new Set();
        if (refreshed) refreshed.forEach((h) => holidaysSet.add(h.holiday_date));
        setSelectedHolidays(holidaysSet);
      } else {
        const holidaysSet = new Set();
        if (data) data.forEach((h) => holidaysSet.add(h.holiday_date));
        setSelectedHolidays(holidaysSet);
      }
    } catch (error) {
      console.error('休日データ読み込みエラー:', error);
    }
  }, []);

  useEffect(() => {
    loadHolidays();
  }, [loadHolidays]);

  const toggleHolidaySelection = (dateStr) => {
    const newSet = new Set(selectedHolidays);
    if (newSet.has(dateStr)) {
      newSet.delete(dateStr);
    } else {
      newSet.add(dateStr);
    }
    setSelectedHolidays(newSet);
  };

  const applyWeekdaySelection = () => {
    if (selectedWeekdays.length === 0) {
      alert('曜日を選択してください。');
      return;
    }

    const today = new Date();
    const month1 = new Date(today.getFullYear(), today.getMonth(), 1);
    const month2 = new Date(today.getFullYear(), today.getMonth() + 1, 1);
    const month3 = new Date(today.getFullYear(), today.getMonth() + 2, 1);

    const months = [month1, month2, month3];
    const newSet = new Set(selectedHolidays);

    months.forEach((monthDate) => {
      const year = monthDate.getFullYear();
      const month = monthDate.getMonth();
      const lastDay = new Date(year, month + 1, 0).getDate();

      for (let day = 1; day <= lastDay; day++) {
        const date = new Date(year, month, day);
        const dayOfWeek = date.getDay();
        if (selectedWeekdays.includes(dayOfWeek)) {
          const dateStr = formatDateForHoliday(date);
          newSet.add(dateStr);
        }
      }
    });

    setSelectedHolidays(newSet);
  };

  const formatDateForHoliday = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const saveHolidays = async () => {
    setLoading(true);
    try {
      // 既存の休日をすべて削除
      const { error: deleteError } = await supabaseClient
        .from('holidays')
        .delete()
        .neq('id', 0);

      if (deleteError) throw deleteError;

      // 選択された休日を追加
      if (selectedHolidays.size > 0) {
        const holidaysToInsert = Array.from(selectedHolidays).map((date) => ({
          holiday_date: date,
        }));

        const { error: insertError } = await supabaseClient
          .from('holidays')
          .insert(holidaysToInsert);

        if (insertError) throw insertError;
      }

      alert(`休日設定を保存しました（${selectedHolidays.size}日）。`);
      await loadHolidays();
    } catch (error) {
      console.error('休日保存エラー:', error);
      alert('休日設定の保存に失敗しました。');
    } finally {
      setLoading(false);
    }
  };

  const removeHoliday = (dateStr) => {
    const newSet = new Set(selectedHolidays);
    newSet.delete(dateStr);
    setSelectedHolidays(newSet);
  };

  const formatDateForDisplay = (dateStr) => {
    const date = new Date(dateStr);
    const weekdays = ['日', '月', '火', '水', '木', '金', '土'];
    return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日（${weekdays[date.getDay()]}）`;
  };

  const sortedDates = Array.from(selectedHolidays).sort();

  return (
    <section className="admin-section">
      <div className="container">
        <div className="settings-form">
          <h3>休日設定</h3>
      <p>カレンダーから日付をクリックして休日を選択してください。曜日で一括選択も可能です。</p>

      {/* 曜日一括選択 */}
      <div className="form-group">
        <label>曜日で一括選択</label>
        <div className="checkbox-group">
          {[
            { value: 0, label: '日曜日' },
            { value: 1, label: '月曜日' },
            { value: 2, label: '火曜日' },
            { value: 3, label: '水曜日' },
            { value: 4, label: '木曜日' },
            { value: 5, label: '金曜日' },
            { value: 6, label: '土曜日' },
          ].map((weekday) => (
            <label key={weekday.value}>
              <input
                type="checkbox"
                className="weekday-selector"
                value={weekday.value}
                checked={selectedWeekdays.includes(weekday.value)}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedWeekdays([...selectedWeekdays, weekday.value]);
                  } else {
                    setSelectedWeekdays(
                      selectedWeekdays.filter((w) => w !== weekday.value)
                    );
                  }
                }}
              />
              {weekday.label}
            </label>
          ))}
        </div>
        <button
          type="button"
          className="btn-secondary"
          onClick={applyWeekdaySelection}
          style={{ marginTop: '10px' }}
        >
          選択した曜日を適用
        </button>
      </div>

      {/* 3か月分のカレンダー */}
      <div className="holiday-calendars-container">
        <HolidayCalendar
          monthIndex={0}
          selectedHolidays={selectedHolidays}
          onToggleHoliday={toggleHolidaySelection}
        />
        <HolidayCalendar
          monthIndex={1}
          selectedHolidays={selectedHolidays}
          onToggleHoliday={toggleHolidaySelection}
        />
        <HolidayCalendar
          monthIndex={2}
          selectedHolidays={selectedHolidays}
          onToggleHoliday={toggleHolidaySelection}
        />
      </div>

      {/* 選択された休日の表示 */}
      <div className="form-group">
        <label>
          選択された休日（<span id="selectedHolidaysCount">{selectedHolidays.size}</span>日）
        </label>
        <div id="selectedHolidaysList" className="selected-holidays-list">
          {selectedHolidays.size === 0 ? (
            <p style={{ color: '#888', margin: 0 }}>選択された休日はありません</p>
          ) : (
            sortedDates.map((dateStr) => (
              <span key={dateStr} className="selected-holiday-tag">
                {formatDateForDisplay(dateStr)}
                <button
                  type="button"
                  className="remove-btn"
                  onClick={() => removeHoliday(dateStr)}
                >
                  ×
                </button>
              </span>
            ))
          )}
        </div>
      </div>

      <button
        id="saveHolidays"
        className="btn-primary"
        onClick={saveHolidays}
        disabled={loading}
      >
        {loading ? '保存中...' : '休日設定を保存'}
      </button>
        </div>
      </div>
    </section>
  );
}

export default Holidays;

